import { z } from 'zod';

/**
 * A recording's words, and recordings made from words (owner, 7 October 2026; migration 0160).
 * The caps are the owner's: 1,000 characters a recording; 20,000 a month per account on the
 * platform's key unless the platform sets otherwise; on the account's own key, what it sets.
 */
export const SPEECH_PER_RECORDING = 1000;

/** Where a recording's words came from. */
export const MediaWordsSourceSchema = z.enum(['typed', 'transcribed', 'generated']);
export type MediaWordsSource = z.infer<typeof MediaWordsSourceSchema>;

export const MediaUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  /** What it says. Null clears it. */
  description: z.string().trim().max(SPEECH_PER_RECORDING).nullable().optional(),
}).strict();
export type MediaUpdate = z.infer<typeof MediaUpdateSchema>;

export const MediaFromTextSchema = z.object({
  kind: z.enum(['prompt', 'greeting', 'announcement']),
  name: z.string().trim().min(1).max(120),
  text: z.string().trim().min(1).max(SPEECH_PER_RECORDING),
  /** The account's house voice when left out. */
  voice_id: z.string().min(1).max(64).optional(),
  voice_name: z.string().max(80).optional(),
}).strict();
export type MediaFromText = z.infer<typeof MediaFromTextSchema>;

export const VoiceStatusSchema = z.object({
  /** Whose ElevenLabs key a recording made now would use; null when there is none. */
  key: z.enum(['account', 'platform']).nullable(),
  account_key: z.object({ hint: z.string().nullable(), last_error: z.string().nullable() }).nullable(),
  voice: z.object({ id: z.string(), name: z.string() }).nullable(),
  per_recording: z.number().int(),
  /** Characters made this month, by whose key. */
  used: z.object({ account: z.number().int(), platform: z.number().int() }),
  caps: z.object({ platform: z.number().int(), own: z.number().int().nullable() }),
  /** A transcription key is there, so words can be filled in from a recording. */
  transcription: z.boolean(),
  /** The one asking is the platform, which sets the cap on its own key. */
  platform_view: z.boolean(),
});
export type VoiceStatus = z.infer<typeof VoiceStatusSchema>;

export const VoiceSchema = z.object({
  voice_id: z.string(), name: z.string(),
  accent: z.string().nullable(), gender: z.string().nullable(), age: z.string().nullable(),
  description: z.string().nullable(), category: z.string().nullable(),
  has_preview: z.boolean(),
});
export type Voice = z.infer<typeof VoiceSchema>;

export const VoiceSettingsSaveSchema = z.object({
  voice_id: z.string().min(1).max(64).nullable().optional(),
  voice_name: z.string().max(80).nullable().optional(),
  /** A cap on the account's own key; null for none. */
  own_monthly_chars: z.number().int().min(0).max(10_000_000).nullable().optional(),
  /** The platform only: the cap on its key for this account. */
  platform_monthly_chars: z.number().int().min(0).max(10_000_000).optional(),
}).strict();
export type VoiceSettingsSave = z.infer<typeof VoiceSettingsSaveSchema>;

export const VoiceKeySaveSchema = z.object({
  secret: z.string().min(8).max(512).optional(),
  model: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,59}$/, 'That is not a model name').optional(),
}).strict();
export type VoiceKeySave = z.infer<typeof VoiceKeySaveSchema>;
